/**
 * DocumentTemplatePage — 文档模板管理
 *
 * Manages document templates (invoice, BL, DO, SOA, etc.)
 *
 * Uses document-templates API:
 *   GET    /api/v1/document-templates?docType=xxx
 *   GET    /api/v1/document-templates/:id
 *   POST   /api/v1/document-templates
 *   PATCH  /api/v1/document-templates/:id
 *   DELETE /api/v1/document-templates/:id
 *   POST   /api/v1/document-templates/:id/render
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  FileText,
  Search,
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  Edit,
  Save,
  X,
  Paintbrush,
  ArrowLeft,
  Star,
  Receipt,
  Ship,
  ClipboardList,
  FileCode,
  Mail,
} from 'lucide-react';
import { TemplateDesigner } from '@/components/template-designer';
import type { DesignTemplate } from '@/components/template-designer';

// ─── Types ─────────────────────────────────────────

interface DocTemplate {
  id: string;
  tenant_id: string;
  doc_type: string;
  name: string;
  description: string | null;
  is_default: boolean;
  html_template: string;
  css_styles: string | null;
  header_html: string | null;
  footer_html: string | null;
  page_size: string;
  orientation: string;
  margins: string;
  variables: string;
  version: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

const DOC_TYPES = [
  { type: 'invoice',   label: 'Invoice',   icon: Receipt },
  { type: 'quotation', label: 'Quotation', icon: ClipboardList },
  { type: 'bl',        label: 'Bill of Lading', icon: Ship },
  { type: 'do',        label: 'Delivery Order', icon: FileText },
  { type: 'soa',       label: 'SOA',       icon: FileCode },
  { type: 'receipt',   label: 'Receipt',   icon: Receipt },
  { type: 'manifest',  label: 'Manifest',  icon: ClipboardList },
  { type: 'email',     label: 'Email',     icon: Mail },
] as const;

// ─── Component ─────────────────────────────────────

export default function DocumentTemplatePage() {
  const { t } = useTranslation();

  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editCss, setEditCss] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [designerMode, setDesignerMode] = useState(false);
  const [designerInitial, setDesignerInitial] = useState<DesignTemplate | undefined>(undefined);
  const [designerTargetId, setDesignerTargetId] = useState<string | null>(null);

  // New template form
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDocType, setNewDocType] = useState('invoice');
  const [newDescription, setNewDescription] = useState('');
  const [newHtml, setNewHtml] = useState('');
  const [newIsDefault, setNewIsDefault] = useState(false);

  // ─── Load templates ──────────────────────────────

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = typeFilter !== 'all' ? `?docType=${typeFilter}` : '';
      const res = await api.get<{ data: DocTemplate[]; total: number }>(`/document-templates${params}`);
      setTemplates(res.data ?? []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ─── Save template edit ──────────────────────────

  const handleSaveEdit = async (tmpl: DocTemplate) => {
    setSaving(true);
    try {
      await api.patch(`/document-templates/${tmpl.id}`, {
        htmlTemplate: editContent,
        cssStyles: editCss,
      });
      setEditingId(null);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Create template ────────────────────────────

  const handleCreate = async () => {
    if (!newName || !newHtml) return;
    setSaving(true);
    try {
      await api.post('/document-templates', {
        docType: newDocType,
        name: newName,
        description: newDescription || undefined,
        htmlTemplate: newHtml,
        isDefault: newIsDefault,
      });
      setShowNew(false);
      setNewName('');
      setNewDescription('');
      setNewHtml('');
      setNewIsDefault(false);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete template ─────────────────────────────

  const handleDelete = async (tmpl: DocTemplate) => {
    setSaving(true);
    try {
      await api.delete(`/document-templates/${tmpl.id}`);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  // ─── Preview render ──────────────────────────────

  const handlePreview = async (tmpl: DocTemplate) => {
    if (previewId === tmpl.id) {
      setPreviewId(null);
      setPreviewHtml('');
      return;
    }
    try {
      const res = await api.post<{ data: { html: string } }>(`/document-templates/${tmpl.id}/render`, {});
      setPreviewHtml(res.data?.html ?? tmpl.html_template);
    } catch {
      setPreviewHtml(tmpl.html_template);
    }
    setPreviewId(tmpl.id);
    setEditingId(null);
  };

  // ─── Filter ──────────────────────────────────────

  const filtered = templates.filter((tmpl) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        tmpl.name.toLowerCase().includes(q) ||
        (tmpl.description ?? '').toLowerCase().includes(q) ||
        tmpl.doc_type.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const getTypeConfig = (type: string) => {
    const found = DOC_TYPES.find((d) => d.type === type);
    return found ?? { type, label: type, icon: FileText };
  };

  // ─── Visual designer ────────────────────────────

  const openDesigner = (tmpl?: DocTemplate) => {
    setDesignerTargetId(tmpl?.id ?? null);
    setDesignerInitial(undefined);
    setDesignerMode(true);
  };

  const handleDesignerSave = async (designTemplate: DesignTemplate) => {
    if (designerTargetId) {
      await api.patch(`/document-templates/${designerTargetId}`, {
        htmlTemplate: JSON.stringify(designTemplate),
      });
    }
    await loadTemplates();
  };

  // ─── Designer mode ──────────────────────────────

  if (designerMode) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/30 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setDesignerMode(false)} className="gap-1">
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('admin.back', 'Back')}
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          <TemplateDesigner
            initialTemplate={designerInitial}
            onSave={handleDesignerSave}
            compact={false}
          />
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary" />
          <h1 className="font-semibold">{t('admin.docTemplate.title', 'Document Templates')}</h1>
          <Badge variant="outline" className="text-xs">{templates.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadTemplates}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {t('admin.refresh', 'Refresh')}
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t('admin.docTemplate.newTemplate', 'New Template')}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t('admin.docTemplate.searchPlaceholder', 'Search templates…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Button
          variant={typeFilter === 'all' ? 'default' : 'ghost'}
          size="sm"
          className="text-xs"
          onClick={() => setTypeFilter('all')}
        >
          {t('admin.all', 'All')}
        </Button>
        {DOC_TYPES.map(({ type, label, icon: Icon }) => (
          <Button
            key={type}
            variant={typeFilter === type ? 'default' : 'ghost'}
            size="sm"
            className="text-xs"
            onClick={() => setTypeFilter(type)}
          >
            <Icon className="h-3 w-3 mr-1" />
            {label}
          </Button>
        ))}
      </div>

      {/* New template form */}
      {showNew && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">{t('admin.docTemplate.newDocTemplate', 'Create Template')}</CardTitle>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowNew(false)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground">{t('admin.docTemplate.name', 'Name')}</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-8 text-xs" placeholder="Invoice Standard" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">{t('admin.docTemplate.type', 'Type')}</label>
                <select
                  value={newDocType}
                  onChange={(e) => setNewDocType(e.target.value)}
                  className="w-full h-8 rounded border text-xs px-2 bg-background"
                >
                  {DOC_TYPES.map((d) => (
                    <option key={d.type} value={d.type}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">{t('admin.docTemplate.description', 'Description')}</label>
                <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} className="h-8 text-xs" placeholder="Optional" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">HTML Template</label>
              <textarea
                value={newHtml}
                onChange={(e) => setNewHtml(e.target.value)}
                className="w-full h-32 rounded border bg-muted/30 p-2 text-xs font-mono resize-none"
                placeholder="<html>&#10;  <body>&#10;    <h1>{{companyName}}</h1>&#10;    <p>Invoice #{{invoiceNumber}}</p>&#10;  </body>&#10;</html>"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="new-is-default"
                checked={newIsDefault}
                onChange={(e) => setNewIsDefault(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="new-is-default" className="text-xs text-muted-foreground">
                {t('admin.docTemplate.setDefault', 'Set as default for this type')}
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
                {t('admin.cancel', 'Cancel')}
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={saving || !newName || !newHtml}>
                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                {t('admin.create', 'Create')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates grid */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">{t('admin.docTemplate.noTemplates', 'No templates found')}</p>
            <p className="text-xs mt-1">{t('admin.docTemplate.noTemplatesHint', 'Create your first document template to get started.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((tmpl) => {
              const typeConfig = getTypeConfig(tmpl.doc_type);
              const TypeIcon = typeConfig.icon;
              const isEditing = editingId === tmpl.id;
              const isPreviewing = previewId === tmpl.id;

              return (
                <Card key={tmpl.id}>
                  <CardContent className="py-3 space-y-2">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{tmpl.name}</span>
                        <Badge variant="outline" className="text-[10px]">{typeConfig.label}</Badge>
                        {tmpl.is_default && (
                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] px-2 text-primary"
                          onClick={() => openDesigner(tmpl)}
                        >
                          <Paintbrush className="h-3 w-3 mr-1" />
                          Designer
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => handlePreview(tmpl)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          {t('admin.preview', 'Preview')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => {
                            if (isEditing) {
                              setEditingId(null);
                            } else {
                              setEditingId(tmpl.id);
                              setEditContent(tmpl.html_template ?? '');
                              setEditCss(tmpl.css_styles ?? '');
                              setPreviewId(null);
                            }
                          }}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          {t('admin.edit', 'Edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => handleDelete(tmpl)}
                          disabled={saving}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="flex gap-3 text-[10px] text-muted-foreground">
                      {tmpl.description && <span>{tmpl.description}</span>}
                      <span>{tmpl.page_size} / {tmpl.orientation}</span>
                      <span>v{tmpl.version}</span>
                    </div>

                    {/* Preview */}
                    {isPreviewing && (
                      <div className="rounded border bg-white p-3 max-h-64 overflow-auto">
                        <div
                          className="text-xs prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: previewHtml || tmpl.html_template }}
                        />
                      </div>
                    )}

                    {/* Editor */}
                    {isEditing && (
                      <div className="space-y-2">
                        <label className="text-[10px] text-muted-foreground">HTML Template</label>
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full h-32 rounded border bg-muted/30 p-2 text-xs font-mono resize-none"
                        />
                        <label className="text-[10px] text-muted-foreground">CSS Styles</label>
                        <textarea
                          value={editCss}
                          onChange={(e) => setEditCss(e.target.value)}
                          className="w-full h-20 rounded border bg-muted/30 p-2 text-xs font-mono resize-none"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                            {t('admin.cancel', 'Cancel')}
                          </Button>
                          <Button size="sm" onClick={() => handleSaveEdit(tmpl)} disabled={saving}>
                            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                            {t('admin.save', 'Save')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
